import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { acceptInvitation as fetchInvitation } from '@/api/enterprise'
import { acceptProjectInvitation, type AcceptResult } from '@/api/team'
import toast from 'react-hot-toast'

// The single landing page for every invitation email. It resolves the token
// BEFORE deciding what the invitee has to do:
//
//   no account yet          → /register?invitation=true (token consumed there)
//   account, not signed in  → /?next=<here> and come back to accept
//   account, signed in      → POST accept, then land in the project
//
// Registration alone can't serve the second and third cases: it refuses an
// email it already knows, so an existing user invited to a project (the
// cross-organization "customer" grant especially, where accepting is what
// provisions their runtime database) had no way to accept at all.
//
// Acceptance finishes in ONE call and asks the invitee for nothing. A customer
// organization is provisioned on the DEFAULT database the platform gives it —
// even for a project pinned to a database of the owner's — so the invitee
// lands in the running application. Moving it into a database of their own is
// offered inside that application (System administration → Database), where it
// is a decision rather than a toll gate on the invitation.

interface InvitationInfo {
  email: string
  tenant_name: string
  role: string
  role_display: string
  token: string
  is_project_invite: boolean
  project_id: string | null
  project_owner_id: string | null
  project_roles: string[]
  has_account: boolean
}

const centered: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  background: 'var(--color-bg)',
  gap: '1.25rem',
  padding: '2rem',
  textAlign: 'center',
}

export default function AcceptInvitationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, authStatus, refreshUser } = useAuth()

  const token = searchParams.get('token') || ''

  const [invite, setInvite] = useState<InvitationInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [accepting, setAccepting] = useState(false)
  // Acceptance provisions databases on the server — never fire it twice for
  // one page load (React strict mode double-invokes effects).
  const acceptedOnce = useRef(false)

  useEffect(() => {
    if (!token) {
      setError('This invitation link is missing its token. Please use the link from your invitation email.')
      return
    }
    fetchInvitation(token)
      .then((data: InvitationInfo) => setInvite(data))
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setError(msg || 'This invitation is no longer valid. Ask for a fresh invitation.')
      })
  }, [token])

  const finish = useCallback(async (res: AcceptResult, projectId: string) => {
    // Membership (and, cross-tenant, the runtime database) exists only now —
    // re-read the session so the app sees the project.
    await refreshUser()
    toast.success(`You now have access to ${projectId}`)
    navigate(res.redirect || '/workbench', { replace: true })
  }, [navigate, refreshUser])

  const accept = useCallback(async (info: InvitationInfo) => {
    if (!info.project_id) return
    setAccepting(true)
    try {
      const res = await acceptProjectInvitation(info.project_id, token)
      await finish(res, info.project_id)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Could not accept this invitation.')
      setAccepting(false)
    }
  }, [finish, token])

  useEffect(() => {
    if (!invite || error || authStatus === 'loading') return

    // Brand-new user — registration accepts the invitation for them.
    if (!invite.has_account) {
      navigate(
        `/register?invitation=true&email=${encodeURIComponent(invite.email)}&token=${encodeURIComponent(token)}`,
        { replace: true },
      )
      return
    }

    // Has an account but isn't signed in — sign in and return here.
    if (authStatus !== 'authed' || !user) {
      const here = `/accept-invitation?token=${encodeURIComponent(token)}`
      navigate(`/?next=${encodeURIComponent(here)}`, { replace: true })
      return
    }

    // A tenant-level (non-project) invitation for someone who already has an
    // account carries nothing left to grant.
    if (!invite.is_project_invite) {
      setError('This invitation is for creating an account, but this email already has one. Nothing to accept.')
      return
    }

    if (user.email.toLowerCase() !== invite.email.toLowerCase()) {
      setError(
        `This invitation was sent to ${invite.email}, but you are signed in as ${user.email}. ` +
        'Sign out and sign in as the invited user to accept it.',
      )
      return
    }

    if (acceptedOnce.current) return
    acceptedOnce.current = true
    accept(invite)
  }, [invite, error, authStatus, user, navigate, token, accept])

  if (error) {
    return (
      <div style={centered}>
        <span className="material-icons" style={{ fontSize: 41, color: 'var(--color-error)' }}>
          error_outline
        </span>
        <h2 style={{ fontSize: '1.3125rem', fontWeight: 600 }}>Invitation not accepted</h2>
        <p style={{ color: 'var(--color-text-secondary)', maxWidth: 460, fontSize: '1rem' }}>
          {error}
        </p>
        <Link to="/">Back to Login</Link>
      </div>
    )
  }

  return (
    <div style={centered}>
      <div className="spinner spinner-lg" />
      <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
        {accepting && invite?.project_id
          ? `Setting up your access to ${invite.project_id}...`
          : 'Checking your invitation...'}
      </p>
    </div>
  )
}
